import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../app/hooks';
import QuestionnaireAnswer from '../Questionnaires/QuestionnnaireAnswer';
import Layout from '../Layout/Layout';
import { fetchPublishedQuestionnaire, fetchSurvey, resetSurvey, updateBPSonCurSurvey } from '../../app/store/surveySlice';
import { Questionnaire } from '../../interfaces/questionnaire.interface';
import {
  resetQuestionnaireAnswer,
  setCompanyCertifications,
  setReadOnly,
  setBPStatusIds,
  fetchSurveyCompany,
  setQuestionAnswerFromPoll,
} from '../../app/store/questionnaireAnswerSlice';
import { Badge, Company, Version } from '../../interfaces/users.interface';

import { statuses } from '../../services/questionnaireQuestionStatus';
import { fetchSurvey as getSurvey } from '../../services/surveys';
import { updateSurvey } from '../../app/store/surveySlice';
import { QuestionAnswer, QuestionnaireAnswerHandle } from '../../interfaces/questionAnswer.interface';
import { BestPracticeStatus } from '../../interfaces/survey.interface';
import { createMultipleBestPracticeStatuses, updateBestPracticeStatus } from '../../services/bestPracticeStatus';
import { resetAssessment } from '../../app/store/assessmentSlice';
import { updateWorkflow } from '../../app/store/workflowSlice';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { QuestionnaireTypes } from '../../services/questionnaires';
import moment from 'moment';
import { DynamicFormOutlined } from '@mui/icons-material';
import { WORKFLOW_STATUSES, WorkflowStatus, workflowVersion } from '../../services/workflow';
import { USER_TYPES } from '../../services/user';
import { fetchApplicationVersion } from '../../app/store/serviceCategories';
import { useValidAssessmentSurveyRelationship } from '../../hooks/useValidAssessmentSurveyRelationship';
import { useGuardedAsync } from '../../hooks/useGuardedAsync';
import { useAssessmentVariant, useAssessmentVariantPending } from '../Assessments/Workspace/useAssessmentVariant';
import { shouldSyncPolledSurveyStatus } from './surveyPolling';
import { resolveSurveyCompanyId, shouldRefetchSurveyCompany } from './surveyCompany';

let surveyUpdate = false;

export default function SurveyExperience() {
  const params = useParams();
  const surveyId:number | undefined = params.surveyId ? parseInt(params.surveyId) : undefined;
  const assessmentId:number | undefined = params.assessmentId ? parseInt(params.assessmentId) : undefined;
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const dispatch = useAppDispatch();
  const newLocal = useAppSelector(state => state.surveys);
  const {
    survey,
    mpa_best_practice: mpaBestPractice,
  } = newLocal;
  const { assessment } = useAppSelector(state => state.assessments);
  useValidAssessmentSurveyRelationship(assessment, survey);
  const {
    showSubmitSurveyButton,
    showSubmitAssessmentButton,
  } = useAppSelector(state => state.secondaryLayoutButtonSlice);
  const { user } = useAppSelector(state => state.user);
  const { bpStatus, preview, questionStatus, assessmentStatus, company, bestPractice, certControlsById } = useAppSelector(state => state.questionnaireAnswer);
  const { applicationVersions } = useAppSelector(state => state.serviceCategories);
  const { workflow } = useAppSelector(state => state.workflow);
  const [siteOrApplicationName, setSiteOrApplicationName] = useState<string | undefined>(undefined);
  const [pollingBegun, setPollingBegun] = useState(false);
  const blueBadge = useMemo(() => (workflow?.badges as Badge[])?.find(b => b.title === 'tpn_self_reported'), [workflow]);
  const fetchingSurveyRef = useRef<number | null>(null);
  const runGuardedAsync = useGuardedAsync(fetchingSurveyRef);
  const companyCertifications = useMemo(() => company?.companyCertifications, [company?.companyCertifications]);

  // Latest-value refs so the polling interval below always compares against the
  // *current* state instead of the values captured when polling began. Without
  // these the interval closed over a stale `survey` and re-dispatched
  // `updateSurvey` (a PATCH) on every 10s tick with no user action (TPE-2887).
  const surveyRef = useRef(survey);
  const questionStatusRef = useRef(questionStatus);
  const workflowRef = useRef(workflow);
  // Guards against dispatching an updateSurvey PATCH while a prior one is still
  // in flight, and against re-firing for a status we have already dispatched.
  const pollUpdateInFlightRef = useRef(false);
  const lastPolledStatusRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    surveyRef.current = survey;
    questionStatusRef.current = questionStatus;
    workflowRef.current = workflow;
  }, [survey, questionStatus, workflow]);

  const handleFetchSurvey = useCallback((sId: number) => {
    return runGuardedAsync(
      sId,
      () => dispatch(fetchSurvey(sId)),
    );
  }, [dispatch, runGuardedAsync]);

  useEffect(() => {
    if (!companyCertifications?.length || !survey?.company) return;

    const companyId = (survey.company as Company).id;
    const badgeTs = blueBadge?.createdAt ? moment(blueBadge.createdAt).valueOf() : Number.POSITIVE_INFINITY;

    companyCertifications
      .filter(cert =>
        cert.company === companyId &&
        cert.adminApproved &&
        (!blueBadge || moment(cert.createdAt).valueOf() < badgeTs) &&
        cert.applicationVersions?.length,
      )
      .forEach(cert => {
        cert.applicationVersions!.forEach(avId => {
          if (applicationVersions.findIndex(x => x.id === avId) === -1) {
            dispatch(fetchApplicationVersion(avId));
          }
        });
      });
  }, [companyCertifications, survey?.company, applicationVersions, blueBadge?.createdAt]);

  useEffect(() => {
    if (
      assessmentId ||
      !surveyId ||
      !bestPractice ||
      !certControlsById ||
      !Object.keys(questionStatus).length ||
      !Object.keys(bpStatus).length ||
      !bpStatus[Object.keys(bpStatus)[0]].id ||
      pollingBegun ||
      (questionnaire && questionnaire.type !== QuestionnaireTypes.MPA_BEST_PRACTICE)
    ) return;

    setPollingBegun(true);
  }, [surveyId, bestPractice, certControlsById, questionStatus, bpStatus]);

  useEffect(() => {
    if (!pollingBegun) return;
    const surveyPoll = setInterval(() => {
      if (!surveyId) return;
      getSurvey(surveyId).then(pollSurvey => {
        // Read current state via refs so each tick compares against the latest
        // values rather than the stale closure captured when polling began.
        const currentSurvey = surveyRef.current;
        const currentQuestionStatus = questionStatusRef.current;
        const currentWorkflow = workflowRef.current;
        (pollSurvey.questionAnswers as QuestionAnswer[]).forEach(qa => {
          if (!currentQuestionStatus || !currentQuestionStatus[qa.question] || currentQuestionStatus[qa.question].questionAnswer?.id) return;
          dispatch(setQuestionAnswerFromPoll(
            qa.question.toString(),
            qa,
            currentWorkflow?.workflowType,
          ));
        });
        // Only PATCH on a genuine, converging status transition: skip while a
        // prior PATCH is in flight and debounce against a status we already
        // dispatched, so the poll no longer fires updateSurvey back-to-back.
        if (
          shouldSyncPolledSurveyStatus(pollSurvey.status, currentSurvey?.status) &&
          !pollUpdateInFlightRef.current &&
          lastPolledStatusRef.current !== pollSurvey.status
        ) {
          lastPolledStatusRef.current = pollSurvey.status;
          pollUpdateInFlightRef.current = true;
          Promise.resolve(dispatch(updateSurvey(pollSurvey))).finally(() => {
            pollUpdateInFlightRef.current = false;
          });
        }
      });
    }, 10000);

    return () => {
      setPollingBegun(false);
      clearInterval(surveyPoll);
    };
  }, [pollingBegun]);

  useEffect(() => {
    if (!user || !questionnaire) return;
    if (user.type !== USER_TYPES.VENDOR_ADMIN && questionnaire.type === QuestionnaireTypes.MPA_BEST_PRACTICE) dispatch(setReadOnly(true));
  }, [user, questionnaire, questionStatus, bpStatus]);

  useEffect(() => {
    if (!params || !surveyId) return;
    if (!survey) dispatch(resetSurvey());
    setQuestionnaire(null);
    // Fully clear the slice; re-injecting the previous company here left the modal PDF using the wrong SP (TPE-2704). It's refetched below.
    dispatch(resetQuestionnaireAnswer());

    if (!survey) {
      handleFetchSurvey(surveyId);
    }

    surveyUpdate = false;
    if (!mpaBestPractice) dispatch(fetchPublishedQuestionnaire(QuestionnaireTypes.MPA_BEST_PRACTICE));
    return () => {
      dispatch(resetSurvey());
      dispatch(resetQuestionnaireAnswer());
      dispatch(resetAssessment());
      setQuestionnaire(null);
      surveyUpdate = false;
    };
  }, [params, surveyId]);

  useEffect(() => {
    if (!survey || questionnaire || survey.id !== surveyId) return;
    setQuestionnaire(survey.questionnaire as Questionnaire);
    if (survey.bestPracticeStatuses?.length) surveyUpdate = false;
  }, [survey, surveyId]);

  useEffect(() => {
    if (!survey) return;
    // Refetch when the slice's company doesn't match this survey's, so the correct SP loads on survey-to-survey navigation (TPE-2704).
    const companyID = resolveSurveyCompanyId(survey);
    if (companyID && shouldRefetchSurveyCompany(companyID, company?.id)) {
      dispatch(fetchSurveyCompany(companyID));
    }
  }, [survey, company]);

  useEffect(() => {
    if (!survey || !company) return;

    if (!company.companyCertifications) return;
    const filteredCompanyCerts = company.companyCertifications.filter(ccert => ccert.company === (survey.company as Company).id && ccert.adminApproved && (!blueBadge || moment(ccert.createdAt).unix() < moment(blueBadge.createdAt).unix()));
    let newCompanyCerts = !survey.application && !survey.site ? filteredCompanyCerts : [];
    if (survey.application && survey.version && applicationVersions.length && company.companyApplications) {
      const companyApplication = company.companyApplications.find(CA => CA.application.id === survey.application);
      if (!companyApplication) return;
      filteredCompanyCerts.forEach(ccert => {
        if (ccert.applicationVersions?.find(c => applicationVersions.find(a => a.id === c && (a.version as Version).value === survey.version))) newCompanyCerts.push(ccert);
      });
      dispatch(setCompanyCertifications(newCompanyCerts));
    }
    if (survey.site) {
      filteredCompanyCerts.forEach(ccert => {
        if (ccert.sites.some(app => app.id === survey.site)) newCompanyCerts.push(ccert);
      });
      dispatch(setCompanyCertifications(newCompanyCerts));
    }

    if (!survey.application && !survey.site) return;
    const site = survey.site;
    const application = survey.application;
    if (site) setSiteOrApplicationName(company.locations?.find(loc => loc.id === site)?.name);
    if (application) {
      const curApp = company.companyApplications?.find(app => app.application.id === application);
      if (!curApp) return setSiteOrApplicationName(undefined);
      setSiteOrApplicationName(`${company.name} ${curApp.application.name} ${survey.version ? survey.version : ''}`);
    }

  }, [company, blueBadge, survey?.id, applicationVersions]);

  const updateSurveywithnewBPStatuses = async (status:string, questionAnswers:QuestionAnswer[], bpStatuses: BestPracticeStatus[]) => {
    if (!survey || !bpStatus) return;
    const bestPracticeStatuses = await createMultipleBestPracticeStatuses(bpStatuses);
    dispatch(setBPStatusIds(bestPracticeStatuses));
    const newQuestionAnswers = [ ...survey.questionAnswers, ...questionAnswers ].map(qa => typeof qa === 'number' ? qa : qa.id);
    dispatch(updateSurvey({ ...survey, status, questionAnswers: [...new Set(newQuestionAnswers)], bestPracticeStatuses }));
    if (workflow) await dispatch(updateWorkflow({ id: workflow.id, status }));
  };

  useEffect(() => {
    if (preview ||
      !survey ||
      !Object.keys(bpStatus).length ||
      !Object.keys(questionStatus).length ||
      !company ||
      !user ||
      (user.type !== USER_TYPES.VENDOR_ADMIN &&  questionnaire?.type === QuestionnaireTypes.MPA_BEST_PRACTICE) ||
      (assessmentStatus && assessmentStatus !== 'Pre-Assessment')
    ) return;
    let newbpStatus = Object.keys(bpStatus).map(bps => bpStatus[bps]);
    let surveyStatus = 'incomplete';
    let newQA:QuestionAnswer[] = [];
    let questionsAnsweredCount = 0;
    let visibleQuestionsCount = 0;
    Object.keys(questionStatus).forEach(id => {
      if (questionStatus[id].questionAnswer) newQA = newQA.concat((questionStatus[id].questionAnswer as QuestionAnswer).id);
    });
    newQA = [ ...new Set([ ...survey.questionAnswers, ...newQA ].map(qa => typeof qa === 'number' ? qa : qa.id)) ];
    Object.keys(bpStatus).forEach(id => {
      if (!id || !bpStatus[id].id || bpStatus[id].status === 'hide') return;
      if ((!surveyStatus || surveyStatus.length === 0) && bpStatus[id].status !== 'hide') surveyStatus = bpStatus[id].status;
      if (statuses.indexOf(surveyStatus) < statuses.indexOf(bpStatus[id].status) && bpStatus[id].status !== 'hide') surveyStatus = bpStatus[id].status;
      questionsAnsweredCount += bpStatus[id].questionsAnsweredCount;
      visibleQuestionsCount += bpStatus[id].visibleQuestionsCount;
    });
    if (questionsAnsweredCount === visibleQuestionsCount && visibleQuestionsCount !== 0 && ['remediation', 'priority', 'submittedForAssessment', 'submitted'].indexOf(surveyStatus) === -1 && surveyStatus.indexOf('remediation') === -1) surveyStatus = 'complete';
    if (survey.status === 'submittedForAssessment' && ['remediation', 'priority', 'submitted'].indexOf(surveyStatus) === -1 && surveyStatus.indexOf('remediation') === -1) surveyStatus = 'submittedForAssessment';
    if (surveyStatus === 'complete' && !!assessment?.createdAt) surveyStatus = 'submittedForAssessment';
    if (!survey.bestPracticeStatuses?.length) {
      if (!surveyUpdate) updateSurveywithnewBPStatuses(surveyStatus, newQA, newbpStatus);
      surveyUpdate = true;
      return;
    }
    if (!newbpStatus.length || !newbpStatus[0].id) {
      dispatch(setBPStatusIds(survey.bestPracticeStatuses as BestPracticeStatus[]));
      return;
    }
    if (
      (survey.status !== surveyStatus && surveyStatus !== 'remediation') ||
      (survey.bestPracticeStatuses && survey.bestPracticeStatuses.length !== newbpStatus.length)
    ) {
      dispatch(updateSurvey({ ...survey, status: surveyStatus, bestPracticeStatuses: newbpStatus }));
      if (workflow && workflow.status !== surveyStatus) {
        dispatch(updateWorkflow({ id: workflow.id, status: surveyStatus }));
      }
    }
  }, [questionStatus, bpStatus]);

  useEffect(() => {
    if (
      !survey ||
      !Object.keys(bpStatus).length ||
      bpStatus[Object.keys(bpStatus)[0]].id
    ) return;
    
    let newbpStatus = Object.keys(bpStatus).map(bps => bpStatus[bps]);
    if (!newbpStatus.length || !newbpStatus[0].id) {
      dispatch(setBPStatusIds(survey.bestPracticeStatuses as BestPracticeStatus[]));
      return;
    }
  }, [bpStatus]);

  useEffect(() => {
    if (!bpStatus || !survey || !survey.bestPracticeStatuses ||
      !user || (user.type !== USER_TYPES.VENDOR_ADMIN &&  questionnaire?.type === QuestionnaireTypes.MPA_BEST_PRACTICE) ||
      (assessmentStatus && assessmentStatus !== 'Pre-Assessment')
    ) return;
    (survey.bestPracticeStatuses as BestPracticeStatus[]).forEach(bps => {
      const bpid = typeof bps.bestPractice === 'number' ? bps.bestPractice : bps.bestPractice.id;
      if (!bpid || !bpStatus[bpid] || bpStatus[bpid].id === undefined) return;
      if (
        bps.questionsAnsweredCount !== bpStatus[bpid].questionsAnsweredCount ||
        bps.visibleQuestionsCount !== bpStatus[bpid].visibleQuestionsCount ||
        bps.status !== bpStatus[bpid].status ||
        bps.color !== bpStatus[bpid].color
      ) {
        updateBestPracticeStatus({
          id: bpStatus[bpid].id,
          bestPractice: bpStatus[bpid].bestPractice,
          color: bpStatus[bpid].color,
          questionsAnsweredCount: bpStatus[bpid].questionsAnsweredCount,
          visibleQuestionsCount: bpStatus[bpid].visibleQuestionsCount,
          status: bpStatus[bpid].status,
        });
        dispatch(updateBPSonCurSurvey({
          id: bpStatus[bpid].id,
          bestPractice: bpStatus[bpid].bestPractice,
          color: bpStatus[bpid].color,
          questionsAnsweredCount: bpStatus[bpid].questionsAnsweredCount,
          visibleQuestionsCount: bpStatus[bpid].visibleQuestionsCount,
          status: bpStatus[bpid].status,
        }));
      }
    });
  }, [bpStatus]);

  const isBaselineSurveyAndComplete = useMemo(() => {
    if (!questionStatus) return false;

    const questionStatusArray = Object.values(questionStatus);

    if (!survey ||
      !survey.questionnaire ||
      ![QuestionnaireTypes.APPLICATION_BASELINE, QuestionnaireTypes.SITE_BASELINE].includes((survey.questionnaire as Questionnaire)?.type || '')
    ) return false;
    // Check that every item is an object and has a non-empty answer
    return questionStatusArray.every(
      (qa) => typeof qa === 'object' && qa !== null &&
        (!!qa.questionAnswer?.answer?.length ||
          qa.status === 'hide' ||
          (qa.question?.questionnaire as Questionnaire)?.id !== (survey.questionnaire as Questionnaire)?.id
        ),
    );
  }, [survey?.questionnaire, questionStatus]);

  const linkTwo = useMemo(() => {
    if (!isBaselineSurveyAndComplete || !workflow?.bpSurvey) return undefined;

    // Assessment exist but in these cases navigate user to questionnaire
    const questionnaireViewOnly: WorkflowStatus[] = [
      WORKFLOW_STATUSES.ASSESSING,
      WORKFLOW_STATUSES.SUBMITTED,
      WORKFLOW_STATUSES.REJECTED,
    ];

    if (workflow?.assessment && !questionnaireViewOnly.includes(workflow.status as WorkflowStatus)) {
      return `/assessment/${workflow.assessment}/questionnaire/${workflow.bpSurvey}/`;
    }

    return `/questionnaire/${workflow.bpSurvey}/`;
  }, [isBaselineSurveyAndComplete, survey, workflow]);

  const version = useMemo(() => {
    return workflowVersion(workflow?.workflowType);
  }, [workflow]);

  // Assessor-experience-v2 renders its own page header inside the workspace.
  const isAssessmentVariant = useAssessmentVariant();
  const variantPending = useAssessmentVariantPending();
  // Withhold the classic chrome until the variant is actually known, or it flashes.
  const showClassicChrome = !isAssessmentVariant && !variantPending;

  const qaRef = useRef<QuestionnaireAnswerHandle>(null);
  const handleSubmitForAssessment = useCallback(() => {
    return qaRef.current?.showSubmitQuestionnaireDialog();
  }, []);

  const handleSubmitAssessment = useCallback(() => {
    return qaRef.current?.showSumbitAssessmentDialog();
  }, []);

  return (
    <Layout
      pageTitle={`${questionnaire ? questionnaire.title : ''}${siteOrApplicationName ? ` for ${siteOrApplicationName}` : ''}`}
      includeHeader={showClassicChrome}
      includeDivider={showClassicChrome}
      link={survey ? user && (user.type === USER_TYPES.TPN_ADMIN || user.type === USER_TYPES.CONTENT_OWNER) ? '/registry/' + (survey.company as Company).id : '/profile' : undefined}
      buttonText='Back to Company Details'
      buttonIcon={ArrowBackIcon}
      secondaryButtonClick={showSubmitSurveyButton ? handleSubmitForAssessment : showSubmitAssessmentButton ? handleSubmitAssessment : undefined}
      linkTwo={linkTwo}
      buttonTextTwo={isBaselineSurveyAndComplete ? `Best Practice ${version}` : showSubmitSurveyButton ? 'Submit for Assessment' : showSubmitAssessmentButton ? 'Submit Assessment' : undefined}
      buttonIconTwo={isBaselineSurveyAndComplete ? DynamicFormOutlined : undefined}
      >
        { questionnaire && <QuestionnaireAnswer ref={qaRef} questionnaire={questionnaire} /> }
      </Layout>
  );
}